import { ApiResponse, LoginResponse, User, UserPasskeyDto, UserSession } from '../../types';
import apiClient from './client';

export const authApi = {
  // Login & Session
  login: async (credentials: { email: string; password: string; rememberMe?: boolean }): Promise<ApiResponse<LoginResponse>> => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  me: async (): Promise<ApiResponse<User>> => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  logout: async (refreshToken?: string): Promise<ApiResponse<{ success: boolean }>> => {
    const response = await apiClient.post('/auth/logout', { refreshToken });
    return response.data;
  },

  // Sessions Management
  getSessions: async (): Promise<ApiResponse<UserSession[]>> => {
    const response = await apiClient.get('/auth/sessions');
    return response.data;
  },

  revokeSession: async (sessionId: string): Promise<ApiResponse<{ success: boolean }>> => {
    const response = await apiClient.delete(`/auth/sessions/${sessionId}`);
    return response.data;
  },

  revokeAllSessions: async (): Promise<ApiResponse<{ success: boolean }>> => {
    const response = await apiClient.delete('/auth/sessions');
    return response.data;
  },

  // Forgot Password
  forgotPassword: async (identifier: string): Promise<ApiResponse<{ message: string; devInfo?: any }>> => {
    const response = await apiClient.post('/auth/forgot-password', { identifier });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    const response = await apiClient.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  // Passkeys (WebAuthn)
  getPasskeys: async (): Promise<ApiResponse<UserPasskeyDto[]>> => {
    const response = await apiClient.get('/auth/passkeys');
    return response.data;
  },

  deletePasskey: async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
    const response = await apiClient.delete(`/auth/passkeys/${id}`);
    return response.data;
  },

  getPasskeyRegisterOptions: async (): Promise<ApiResponse<any>> => {
    const response = await apiClient.post('/auth/passkey/register/options');
    return response.data;
  },

  verifyPasskeyRegister: async (payload: { expectedChallenge: string; registrationResponse: any; deviceLabel?: string }): Promise<ApiResponse<any>> => {
    const response = await apiClient.post('/auth/passkey/register/verify', payload);
    return response.data;
  },

  getPasskeyLoginOptions: async (email?: string): Promise<ApiResponse<any>> => {
    const response = await apiClient.post('/auth/passkey/login/options', { email });
    return response.data;
  },

  verifyPasskeyLogin: async (payload: { expectedChallenge: string; authenticationResponse: any; rememberMe?: boolean }): Promise<ApiResponse<LoginResponse>> => {
    const response = await apiClient.post('/auth/passkey/login/verify', payload);
    return response.data;
  },

  // TOTP 2FA
  getTotpStatus: async (): Promise<ApiResponse<{ totpEnabled: boolean }>> => {
    const response = await apiClient.get('/auth/totp/status');
    return response.data;
  },

  setupTotp: async (): Promise<ApiResponse<{ secret: string; qrCodeUrl: string }>> => {
    const response = await apiClient.post('/auth/totp/setup');
    return response.data;
  },

  verifySetupTotp: async (code: string): Promise<ApiResponse<{ success: boolean; backupCodes: string[] }>> => {
    const response = await apiClient.post('/auth/totp/verify-setup', { code });
    return response.data;
  },

  verifyTotpLogin: async (mfaToken: string, code: string): Promise<ApiResponse<LoginResponse>> => {
    const response = await apiClient.post('/auth/totp/verify-login', { mfaToken, code });
    return response.data;
  },

  disableTotp: async (password: string, code: string): Promise<ApiResponse<{ success: boolean }>> => {
    const response = await apiClient.post('/auth/totp/disable', { password, code });
    return response.data;
  },

  regenerateBackupCodes: async (code: string): Promise<ApiResponse<{ backupCodes: string[] }>> => {
    const response = await apiClient.post('/auth/totp/backup-codes/regenerate', { code });
    return response.data;
  },
};

export default authApi;
